import { create } from 'zustand';
import type { CombatActionResponseDTO } from '@hexcrawl/shared-utils';
import type { EnqueueOptions } from '../services/turnEventQueue';

export type CombatInputMode = 'movement' | 'target-selection';

type ResponseHandler = (response: CombatActionResponseDTO, options?: EnqueueOptions) => Promise<void>;

interface CombatUIState {
  inputMode: CombatInputMode;
  selectedAbilityId: string | null;
  /**
   * Set alongside `selectedAbilityId` when the pending cast comes from a consumable
   * rather than the character's own ability bar. Targeting and highlighting still key
   * off the ability; only the request sent on confirm differs.
   */
  selectedItemInstanceId: string | null;
  queueBusy: boolean;
  combatOutcome: 'victory' | 'defeat' | null;

  setInputMode: (mode: CombatInputMode) => void;
  selectAbility: (abilityId: string) => void;
  selectItemAbility: (instanceId: string, abilityId: string) => void;
  cancelAbilitySelection: () => void;
  reset: () => void;

  setQueueBusy: (busy: boolean) => void;
  setCombatOutcome: (outcome: 'victory' | 'defeat' | null) => void;

  /** Set by CombatScene to route responses through the event queue. */
  enqueueResponse: ResponseHandler | null;
  setEnqueueResponse: (handler: ResponseHandler | null) => void;
}

export const useCombatStore = create<CombatUIState>()((set) => ({
  inputMode: 'movement',
  selectedAbilityId: null,
  selectedItemInstanceId: null,
  queueBusy: false,
  combatOutcome: null,

  setInputMode: (mode) => set({ inputMode: mode }),

  selectAbility: (abilityId) => set({
    inputMode: 'target-selection',
    selectedAbilityId: abilityId,
    selectedItemInstanceId: null,
  }),

  selectItemAbility: (instanceId, abilityId) => set({
    inputMode: 'target-selection',
    selectedAbilityId: abilityId,
    selectedItemInstanceId: instanceId,
  }),

  cancelAbilitySelection: () => set({
    inputMode: 'movement',
    selectedAbilityId: null,
    selectedItemInstanceId: null,
  }),

  reset: () => set({
    inputMode: 'movement',
    selectedAbilityId: null,
    selectedItemInstanceId: null,
    queueBusy: false,
    combatOutcome: null,
  }),

  setQueueBusy: (busy) => set({ queueBusy: busy }),
  setCombatOutcome: (outcome) => set({ combatOutcome: outcome }),

  enqueueResponse: null,
  setEnqueueResponse: (handler) => set({ enqueueResponse: handler }),
}));
